import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Crown, Loader2, ArrowRight, AlertTriangle } from 'lucide-react'
import type { PropertyGroupRow } from '@/lib/groups'

interface EditGroupDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  group: PropertyGroupRow | null
  onClose: () => void
}

// CHANGE THE LEAD LISTING: the one edit a combined inventory takes. There is no rename: a combined
// inventory is always called by its lead listing's name (server: PropertyGroup#name), so
// making another listing the lead renames it too, and the dialog says so.
//
// Promotion gets its own in-dialog warning, because it is not a cosmetic
// change of who is listed first. The lead owns the unit's configuration — pricing settings, seasons,
// day-of-week, guardrails, targets, events, market position — and nothing is copied between hotels
// when the crown moves: the old lead keeps its own rows untouched (which is what makes promoting back
// a clean reversal), and the NEW lead's own saved configuration takes over. An operator who promotes
// without knowing that has just repriced every hotel in the group from a configuration they have
// probably never opened. UpdatePropertyGroupMutation's own docs require this to be said before
// confirming; it is said here, and only when the selection actually changes.
export default function EditGroupDialog({ settingsPath, group, onClose }: EditGroupDialogProps) {
  const [leadId, setLeadId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setLeadId(group?.leadPropertyId ?? null)
    setSubmitting(false)
  }, [group?.id, group?.leadPropertyId])

  const currentLead = group?.members.find((member) => member.isLead) ?? null
  const nextLead = group?.members.find((member) => member.id === leadId) ?? null
  const promoting = !!group && leadId !== null && leadId !== group.leadPropertyId
  const canSubmit = !!group && !submitting && promoting

  function submit() {
    if (!group || !canSubmit) return
    setSubmitting(true)
    router.patch(
      `${settingsPath}/groups/${group.id}`,
      { lead_property_id: leadId },
      { onFinish: () => setSubmitting(false), onSuccess: onClose, preserveScroll: true },
    )
  }

  return (
    <Dialog open={group !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Change the lead listing</DialogTitle>
          <DialogDescription>
            Make another listing in the combined inventory led by {group?.leadPropertyName} the lead. A
            combined inventory is called by its lead listing&apos;s name, so it takes the new lead&apos;s name.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-group-lead">Lead listing</Label>
            <Select value={leadId ? String(leadId) : undefined} onValueChange={(value) => setLeadId(Number(value))}>
              <SelectTrigger id="edit-group-lead">
                <SelectValue placeholder="Choose the lead listing" />
              </SelectTrigger>
              <SelectContent>
                {(group?.members ?? []).map((member) => (
                  <SelectItem key={member.id} value={String(member.id)}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only a listing already in this combined inventory can lead it. To make another listing the lead, add it
              first.
            </p>
          </div>

          {promoting && (
            <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
              <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>Every listing will be priced from {nextLead?.name}&apos;s own settings.</span>
              </p>
              <p className="mt-2 flex flex-wrap items-center gap-1.5 pl-6 text-xs text-amber-800">
                <span className="font-medium">{currentLead?.name}</span>
                <ArrowRight className="h-3 w-3 shrink-0 text-amber-500" />
                <span className="font-medium">{nextLead?.name}</span>
              </p>
              <p className="mt-2 pl-6 text-xs leading-relaxed text-amber-800">
                Base rates, seasons, day-of-week rules, guardrails, targets, events and market position for every listing
                will come from {nextLead?.name} instead of {currentLead?.name}. Nothing is copied across and nothing is
                deleted: {currentLead?.name} keeps its own settings exactly as they are, so making it the lead again
                restores them. The combined inventory will be called {nextLead?.name}.
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-lg"
            onClick={submit}
            disabled={!canSubmit}
            title={promoting ? undefined : 'Choose another listing to lead.'}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Crown className="mr-1.5 h-3.5 w-3.5" />
                {promoting ? `Make ${nextLead?.name} the lead` : 'Change lead listing'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
